import { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import { toast } from "sonner";
import { Palette, Save, RotateCcw, Calendar, Users, Sparkles, Heart, Check, Database } from "lucide-react";

const PRESETS = [
  {
    id: "rose-cream",
    name: "Rosa & Crema",
    description: "Elegante, cálido, ideal para bodas y eventos sociales",
    colors: {
      primary: "#D65F79", primary_dark: "#B84A63", primary_soft: "#FDF2F5",
      accent: "#C49557", accent_soft: "#FBF5EC",
      surface: "#FDFBF7", surface_card: "#FFFFFF", surface_muted: "#F7F4EE",
      text_primary: "#1F1B16", text_muted: "#6B6258", border: "#EAE3D9",
    }
  },
  {
    id: "emerald-pearl",
    name: "Esmeralda & Perla",
    description: "Sofisticado, fresco, para catering corporativo",
    colors: {
      primary: "#2E8F5A", primary_dark: "#1F6F44", primary_soft: "#E8F5EE",
      accent: "#C49557", accent_soft: "#FBF5EC",
      surface: "#FAFBF8", surface_card: "#FFFFFF", surface_muted: "#EFF2EC",
      text_primary: "#1A2620", text_muted: "#566660", border: "#DDE5DC",
    }
  },
  {
    id: "midnight-gold",
    name: "Medianoche & Oro",
    description: "Lujo, moderno, premium para eventos de gala",
    colors: {
      primary: "#C49557", primary_dark: "#A07535", primary_soft: "#FBF5EC",
      accent: "#D65F79", accent_soft: "#FDF2F5",
      surface: "#FAF7F2", surface_card: "#FFFFFF", surface_muted: "#F2EDE3",
      text_primary: "#1A1612", text_muted: "#5C5147", border: "#E5DECF",
    }
  },
  {
    id: "ocean-sand",
    name: "Océano & Arena",
    description: "Sereno, costero, ideal para eventos de playa",
    colors: {
      primary: "#5174B5", primary_dark: "#3E5C99", primary_soft: "#EAF0FB",
      accent: "#C49557", accent_soft: "#FBF5EC",
      surface: "#FAFBFD", surface_card: "#FFFFFF", surface_muted: "#EEF1F6",
      text_primary: "#1A2030", text_muted: "#566580", border: "#DCE2EC",
    }
  },
  {
    id: "blossom-mauve",
    name: "Lavanda & Malva",
    description: "Romántico, delicado, perfecto para XV años",
    colors: {
      primary: "#A567B5", primary_dark: "#854F94", primary_soft: "#F5ECF8",
      accent: "#D65F79", accent_soft: "#FDF2F5",
      surface: "#FBF8FC", surface_card: "#FFFFFF", surface_muted: "#F3EEF6",
      text_primary: "#241B26", text_muted: "#6B5A70", border: "#E8DEEC",
    }
  },
  {
    id: "charcoal-coral",
    name: "Carbón & Coral",
    description: "Audaz, contemporáneo, energético",
    colors: {
      primary: "#E07856", primary_dark: "#C25F3D", primary_soft: "#FBEDE7",
      accent: "#3A3A3A", accent_soft: "#EFEFEF",
      surface: "#F9F8F6", surface_card: "#FFFFFF", surface_muted: "#EFEEEB",
      text_primary: "#1B1816", text_muted: "#605A55", border: "#E2DFD9",
    }
  },
];

const Branding = () => {
  const { branding, setBranding, saveBranding } = useTheme();
  const [draft, setDraft] = useState(branding);
  const [saving, setSaving] = useState(false);

  const handleField = (k, v) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    setBranding(next); // live preview
  };

  const applyPreset = (preset) => {
    const next = { ...draft, ...preset.colors };
    setDraft(next);
    setBranding(next);
    toast.success(`Paleta "${preset.name}" aplicada`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveBranding(draft);
      toast.success("Marca y colores guardados");
    } catch (e) {
      toast.error("Error al guardar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const [seeding, setSeeding] = useState(false);
  const cargarPlantillaCelebraPetit = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/seed/celebra-petit`);
      const c = data.creados || {};
      toast.success(`Plantilla cargada: ${c.experiencias || 0} experiencias, ${c.menus || 0} menús, ${c.extras || 0} extras`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setSeeding(false); }
  };


  const reset = () => {
    applyPreset(PRESETS[0]);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--brand-primary)" }}>
            <Palette className="inline w-3.5 h-3.5 mr-1" /> Identidad visual
          </p>
          <h1 className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--brand-text)" }}>
            Marca & Colores
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--brand-text-muted)" }}>
            Personaliza tu sistema operativo de eventos. Los cambios aplican en tiempo real.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="eo-btn eo-btn-outline" data-testid="btn-reset">
            <RotateCcw className="w-4 h-4" /> Restablecer
          </button>
          <button onClick={handleSave} disabled={saving} className="eo-btn eo-btn-primary" data-testid="btn-guardar-branding">
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      {/* PRESETS */}
      <section className="eo-animate-in">
        <h2 className="font-display text-2xl mb-3" style={{ color: "var(--brand-text)" }}>Paletas predefinidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PRESETS.map((p) => {
            const isActive = draft.primary === p.colors.primary;
            return (
              <button key={p.id} onClick={() => applyPreset(p)} className="eo-card eo-card-hover p-4 text-left" style={{ borderColor: isActive ? p.colors.primary : "var(--brand-border)", borderWidth: isActive ? 2 : 1 }} data-testid={`preset-${p.id}`}>
                <div className="flex items-center justify-between mb-2.5">
                  <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>{p.name}</p>
                  {isActive && <Check className="w-5 h-5" style={{ color: p.colors.primary }} />}
                </div>
                <div className="flex gap-1.5 mb-2">
                  {[p.colors.primary, p.colors.primary_dark, p.colors.accent, p.colors.surface, p.colors.text_primary].map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded-lg shadow-sm" style={{ background: c, border: c.includes("FF") || c === "#FFFFFF" ? "1px solid var(--brand-border)" : "none" }} />
                  ))}
                </div>
                <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{p.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* CUSTOM COLORS */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 eo-card p-6 eo-animate-in">
          <h2 className="font-display text-2xl mb-4" style={{ color: "var(--brand-text)" }}>Personaliza tu paleta</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ColorField label="Primario (CTAs)" field="primary" value={draft.primary} onChange={handleField} />
            <ColorField label="Primario oscuro" field="primary_dark" value={draft.primary_dark} onChange={handleField} />
            <ColorField label="Acento" field="accent" value={draft.accent} onChange={handleField} />
            <ColorField label="Fondo principal" field="surface" value={draft.surface} onChange={handleField} />
            <ColorField label="Tarjetas" field="surface_card" value={draft.surface_card} onChange={handleField} />
            <ColorField label="Texto principal" field="text_primary" value={draft.text_primary} onChange={handleField} />
            <ColorField label="Texto suave" field="text_muted" value={draft.text_muted} onChange={handleField} />
            <ColorField label="Bordes" field="border" value={draft.border} onChange={handleField} />
          </div>

          <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--brand-border)" }}>
            <h3 className="font-display text-lg mb-3" style={{ color: "var(--brand-text)" }}>Identidad</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="Nombre comercial" field="brand_name" value={draft.brand_name} onChange={handleField} />
              <TextField label="Tagline / lema" field="tagline" value={draft.tagline} onChange={handleField} />
              <TextField label="URL de logo (opcional)" field="logo_url" value={draft.logo_url} onChange={handleField} className="md:col-span-2" />
            </div>
          </div>

          <div className="mt-6 pt-6" style={{ borderTop: "1px solid var(--brand-border)" }}>
            <h3 className="font-display text-lg mb-3" style={{ color: "var(--brand-text)" }}>Forma</h3>
            <div className="grid grid-cols-2 gap-3">
              <SliderField label="Radio de tarjetas" field="radius_card" value={draft.radius_card} onChange={handleField} min={0} max={28} />
              <SliderField label="Radio de botones" field="radius_button" value={draft.radius_button} onChange={handleField} min={0} max={20} />
            </div>
          </div>
        </div>

        {/* LIVE PREVIEW */}
        <div className="lg:col-span-2 eo-animate-in">
          <div className="sticky top-4 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>Vista previa en vivo</p>
            <Preview branding={draft} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ColorField = ({ label, field, value, onChange }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1.5" style={{ color: "var(--brand-text-muted)" }}>{label}</label>
    <div className="flex items-center gap-2 rounded-xl border p-1.5" style={{ borderColor: "var(--brand-border)", background: "var(--brand-surface-card)" }}>
      <input
        type="color"
        value={value || "#000000"}
        onChange={(e) => onChange(field, e.target.value)}
        className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
        data-testid={`color-${field}`}
      />
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        className="flex-1 px-2 py-1 text-sm font-mono border-0 focus:outline-none bg-transparent uppercase"
        style={{ color: "var(--brand-text)" }}
      />
    </div>
  </div>
);

const TextField = ({ label, field, value, onChange, className = "" }) => (
  <div className={className}>
    <label className="text-[10px] uppercase tracking-[0.14em] block mb-1.5" style={{ color: "var(--brand-text-muted)" }}>{label}</label>
    <input type="text" value={value || ""} onChange={(e) => onChange(field, e.target.value)} className="eo-input" data-testid={`text-${field}`} />
  </div>
);

const SliderField = ({ label, field, value, onChange, min, max }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.14em] flex items-center justify-between mb-1.5" style={{ color: "var(--brand-text-muted)" }}>
      <span>{label}</span>
      <span style={{ color: "var(--brand-text)" }}>{value}px</span>
    </label>
    <input type="range" min={min} max={max} value={value || 0} onChange={(e) => onChange(field, parseInt(e.target.value))} className="w-full" data-testid={`slider-${field}`} />
  </div>
);

const Preview = ({ branding }) => (
  <div className="space-y-3">
    {/* Card preview */}
    <div className="eo-card overflow-hidden">
      <div className="px-5 py-4" style={{ background: `linear-gradient(135deg, ${branding.primary_soft}, ${branding.accent_soft})` }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: branding.primary }}>Vista previa</p>
            <h3 className="font-display text-2xl" style={{ color: branding.text_primary }}>Boda Ana & Carlos</h3>
            <p className="text-xs mt-0.5" style={{ color: branding.text_muted }}>Sábado 14 · 17:00 hrs</p>
          </div>
          <span className="px-3 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.success_soft, color: branding.success }}>Confirmado</span>
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs" style={{ color: branding.text_muted }}>
          <Calendar className="w-3.5 h-3.5" /> <span>Salón Jardín Real</span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: branding.text_muted }}>
          <Users className="w-3.5 h-3.5" /> <span>180 invitados</span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: branding.text_muted }}>
          <Heart className="w-3.5 h-3.5" /> <span>Tema: Botánico clásico</span>
        </div>
      </div>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: `1px solid ${branding.border}`, background: branding.surface_muted }}>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: branding.text_muted }}>Saldo</span>
        <span className="font-display text-base" style={{ color: branding.success }}>$0</span>
      </div>
    </div>

    {/* Button samples */}
    <div className="eo-card p-4 space-y-2">
      <button className="w-full px-4 py-2.5 rounded-xl text-white text-sm font-medium" style={{ background: branding.primary, borderRadius: `${branding.radius_button}px` }}>
        <Sparkles className="inline w-4 h-4 mr-1" /> Botón primario
      </button>
      <button className="w-full px-4 py-2.5 rounded-xl text-sm font-medium" style={{ background: branding.accent, color: "#fff", borderRadius: `${branding.radius_button}px` }}>
        Botón acento
      </button>
      <button className="w-full px-4 py-2.5 rounded-xl text-sm font-medium border" style={{ borderColor: branding.border, color: branding.text_primary, borderRadius: `${branding.radius_button}px`, background: branding.surface_card }}>
        Botón outline
      </button>
    </div>

    {/* Pills sample */}
    <div className="eo-card p-4 flex flex-wrap gap-1.5">
      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.primary_soft, color: branding.primary_dark }}>Primario</span>
      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.accent_soft, color: "#8C6735" }}>Acento</span>
      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.success_soft, color: branding.success }}>Confirmado</span>
      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.warning_soft, color: "#8C5E0B" }}>Producción</span>
      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: branding.info_soft, color: branding.info }}>Nuevo</span>
    </div>
  </div>
);

export default Branding;
